import { Wallet } from 'lucide-react'
import StatusBadge from '@/components/StatusBadge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { PurchaseOrder, Payment, Adjustment } from '@/types'

interface Props {
  balance: string
  outstanding_estimates: string
  total_payments: string
  total_finalized: string
  net_adjustments: string
  purchase_orders: PurchaseOrder[]
  payments: Payment[]
  adjustments: Adjustment[]
}

export default function StatementPublic({
  balance,
  outstanding_estimates,
  total_payments,
  total_finalized,
  net_adjustments,
  purchase_orders,
  payments,
  adjustments,
}: Props) {
  const balanceNum = parseFloat(balance)

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-10">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary/15">
            <Wallet className="size-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">Open Tab</h1>
            <p className="text-xs text-muted-foreground">Account Statement</p>
          </div>
        </div>

        {/* Balance summary */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-10">
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground">Balance</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${balanceNum >= 0 ? 'text-positive' : 'text-destructive'}`}>
                {formatCurrency(balanceNum)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground">Outstanding Estimates</p>
              <p className="text-2xl font-bold font-mono mt-1 text-warning">
                {formatCurrency(parseFloat(outstanding_estimates))}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground">Total Payments</p>
              <p className="text-2xl font-bold font-mono mt-1 text-foreground">
                {formatCurrency(parseFloat(total_payments))}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground">Total Finalized</p>
              <p className="text-2xl font-bold font-mono mt-1 text-foreground">
                {formatCurrency(parseFloat(total_finalized))}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground">Net Adjustments</p>
              <p className="text-2xl font-bold font-mono mt-1 text-foreground">
                {formatCurrency(parseFloat(net_adjustments))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Finalized POs */}
        <Card className="border-border/50 mb-6">
          <CardHeader>
            <CardTitle className="text-base">Finalized Purchase Orders</CardTitle>
          </CardHeader>
          <CardContent>
            {purchase_orders.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No finalized purchase orders
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground">ID</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchase_orders.map((po) => (
                    <TableRow key={po.id} className="border-border/30">
                      <TableCell className="font-medium">#{po.id}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(parseFloat(po.amount))}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={po.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(po.date)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Payments */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base">All Payments</CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No payments recorded
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Linked PO</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} className="border-border/30">
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(parseFloat(payment.amount))}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(payment.date)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {payment.purchase_order_id
                          ? `PO #${payment.purchase_order_id}`
                          : '--'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {payment.notes || '--'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Adjustments */}
        <Card className="border-border/50 mt-6">
          <CardHeader>
            <CardTitle className="text-base">Adjustments</CardTitle>
          </CardHeader>
          <CardContent>
            {adjustments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No adjustments recorded
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground">Type</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Linked PO</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adjustments.map((adjustment) => (
                    <TableRow key={adjustment.id} className="border-border/30">
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            adjustment.adjustment_type === 'credit'
                              ? 'bg-positive/10 text-positive'
                              : 'bg-destructive/10 text-destructive'
                          }`}
                        >
                          {adjustment.adjustment_type === 'credit' ? 'Credit' : 'Debit'}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(parseFloat(adjustment.amount))}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(adjustment.date)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {adjustment.purchase_order_id
                          ? `PO #${adjustment.purchase_order_id}`
                          : '--'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {adjustment.notes || '--'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-xs text-muted-foreground">
            Generated by Open Tab Balance Ledger
          </p>
        </div>
      </div>
    </div>
  )
}
